/**
 * ★ A DELEGAÇÃO no modo `responses` — [spec-008 T032], US-4/US-5. Terceiro
 * irmão de `transporte-tempo-real.js`: aqui não há `RTCPeerConnection`, nem
 * `apiVoz`, nem DOM. Entra uma demanda TIPADA vinda do canal, sai uma chamada
 * ao `POST /chat` de sempre e um `function_call_output` de volta ao fio.
 *
 * ## O laço, como ele foi MEDIDO (G-12 §4-5, G-13 §3-4)
 *
 * ```
 * ←  response.event { delegation_id, event: response.output_item.done }
 *                     item = { type:'function_call', name, arguments, call_id }
 *    …  POST /chat (o cérebro e o RBAC continuam na AAWZ)  …
 * →  response.item.create { item:{ type:'function_call_output', call_id, output } }
 * →  response.create
 * ```
 *
 * `arguments` chega como **string JSON** (`"{\"cliente\":\"Teste Alpha\"}"`),
 * `output` sai como **string**, e o laço se amarra pelo `call_id` — nunca pelo
 * `delegation_id`, que nos dois eventos volta `Unknown parameter`.
 *
 * As FORMAS do fio (ler a demanda, montar o resultado, o `response.create` e os
 * `*.append`, com a recusa do `delegation_id`) moram no irmão
 * `transporte-tempo-real-protocolo.js`, junto dos outros fatos do provedor —
 * aqui fica a POLÍTICA: o que se delega, quando se recusa e o que a voz diz.
 *
 * ## As quatro regras, e por que cada uma é um invariante e não um cuidado
 *
 * | # | regra medida | o que acontece se alguém a violar |
 * |---|---|---|
 * | **N19** | `delegation_id` **nunca** vai ao fio: nos `*.append` ele é `null`, e no laço não existe | `Unknown client delegation.` em 6/6 e o canal MUDO (RMS 0,08) — falha que o provedor não grita, só emudece. Por isso a recusa é NOSSA, antes do envio |
 * | **N20** | **serial por sessão**: com um `function_call` pendente, `response.create` é recusado (*"Submit the pending function call outputs before response.create."*) | a segunda demanda trava a sessão inteira em vez de só a si mesma |
 * | **N21** | `arguments` **desambigua, nunca faz lookup** — `Teste Alfa` chegou `"Teste Alpha"`/`"Teste Z"`/`"cliente teste alpha"` em 7/8 medições | chave de busca derivada acha o cliente ERRADO (falha silenciosa) ou ninguém. O que vira demanda é o TURNO TRANSCRITO; sem turno, recusa-se |
 * | **N16 (D-13)** | o dado de negócio vai ao **CARTÃO**, que é a fonte auditável; a fala é resumo | `NTN-B 2035` → `NTN-B` no canal falado (G-11): valor certo atribuído ao papel errado, que é pior que valor errado |
 *
 * ⚠️ **[T072] A N16 mudou de forma, não de fundo (D-O-6).** A voz deixou de
 * apenas *remeter* e passou a **consolidar o texto que está na tela** — pedido
 * do owner, 17/09/2026. O que continua inteiro: o cartão guarda o valor
 * exato, o cliente, o ativo, o período e a data-base; o número que a voz diz é
 * o que NOSSO formatador produziu (D-O-3); e o único conteúdo que atravessa é
 * o que o consultor **já está lendo**. O irmão
 * `transporte-tempo-real-consolidacao.js` é dono dessa saída e da guarda dela.
 *
 * ## ★ O modo NÃO é escolhido aqui — e o navegador não pode escolhê-lo
 *
 * `delegation.type` vive no corpo de **criação** da sessão e `session.update`
 * só o re-afirma (`The delegation type cannot change after session startup.`,
 * G-12 §2). Além disso `delegation.responses` exige `model` e carrega `tools`
 * (o JSON Schema das funções) — decisões do SERVIDOR, que é quem monta o
 * catálogo (`core/voz_tempo_real_openai.py::catalogo_do_turno_falado`).
 * Mandar esse objeto daqui seria o navegador declarando a configuração do
 * provedor: a segunda autoridade que a D-10 existe para impedir.
 *
 * O que sobra para este lado, e é o que está implementado: **conferir** o modo
 * pelas duas testemunhas do próprio provedor e **cair alto** quando não for
 * `responses` — porque em `client` nenhum `function_call` chega, e o consultor
 * ficaria esperando por uma consulta que ninguém vai receber.
 *
 * ⚠️ **[T050] O parágrafo que vivia aqui MENTIA — e mandava depurar o lugar
 * errado.** Ele dizia que `voz_tempo_real_openai.py::abrir_sessao` montava o
 * corpo *"sem `session.delegation`"* e que por isso *"toda sessão em tempo
 * real vai cair por `MOTIVO_MODO_DE_DELEGACAO`"*. É falso desde a [T010b]:
 * `voz_tempo_real_openai.py:355` manda `'delegation': delegacao_da_sessao(…)`
 * no corpo de CRIAÇÃO.
 *
 * Verificado em sessão REAL, num navegador de verdade ([T050], Chrome +
 * microfone falso, `~/aawz-tmp/spec008-t050/eventos-browser.json`): com o
 * corpo que o nosso backend monta, `session.started` ecoa
 * `delegation.type: "responses"` (2,4 s), `session.delegation.created` traz
 * `target: "responses"` (12,5 s) e o `function_call` chega em **13,4 s** com
 * `{"assunto": "…"}` e `call_id`. As duas testemunhas do modo passam, e as
 * duas portas que as leem (`_aoIniciarSessao`/`_aoCriarDelegacao`) não
 * derrubam nada.
 *
 * ## O que este módulo NÃO faz (e de quem é)
 *
 * - **o cartão** e o encaminhamento do pedido falado de escrita — [T033];
 * - **separar fala × áudio × job** no `interromper` — [T036], no irmão
 *   `transporte-tempo-real-fala.js`;
 * - **falar DURANTE a espera** — [T063], no irmão
 *   `transporte-tempo-real-acompanhamento.js`. Daqui sai só o `comecar()`
 *   quando a consulta entra em voo e o `parar()` quando ela desfecha ou
 *   aborta; a cadência medida e as frases moram lá.
 *
 * ## ★ [T040b] A outra metade do N21: reconciliar antes de consultar
 *
 * A [T032] fechou a metade dura (`arguments` não substitui o turno falado).
 * A metade que falta é a contradição: quando o identificador da pista NÃO
 * aparece no que o consultor falou, a voz **pergunta** em vez de adivinhar. A
 * regra é pura e mora em `transporte-tempo-real-identificador.js`; aqui entra
 * só a recusa nomeada e a saída congelada (`SAIDA_PEDE_IDENTIFICADOR`).
 */
import { formatarParaFala } from './formatar-valor-para-voz'
import { marcarFalhaDaMedicao } from './instrumentacao-de-medicao'
import { criarAcompanhamentoDaEspera } from './transporte-tempo-real-acompanhamento'
import { reconciliarIdentificador } from './transporte-tempo-real-identificador'
import {
  conferirQueRemete,
  falaDoDesfecho,
  lerValorDoResultado,
  montarSaidaComValor,
  montarSaidaDeConsolidacao,
  SAIDA_DECISAO_NA_TELA,
  SAIDA_DE_FALHA,
  SAIDA_DE_RECUSA_POR_CONCORRENCIA,
  SAIDA_EM_ANDAMENTO,
  SAIDA_PEDE_IDENTIFICADOR,
  SAIDA_SEM_TURNO,
} from './transporte-tempo-real-remissoes'
import {
  ErroDeVozTempoReal,
  EVENTO_APPEND_DE_COMENTARIO,
  lerDemandaDeFuncao,
  montarAppendDeFala,
  MODO_DELEGACAO_RESPONSES,
  montarPedidoDeResposta,
  montarResultadoDaFuncao,
  MOTIVO_DELEGACAO_EM_CURSO,
  MOTIVO_DELEGACAO_FALHOU,
  MOTIVO_DEMANDA_SEM_TURNO,
  MOTIVO_IDENTIFICADOR_NAO_CONFERE,
  MOTIVO_MODO_DE_DELEGACAO,
  ORIGEM_VOZ_TEMPO_REAL,
} from './transporte-tempo-real-protocolo'

/**
 * ★ [T073] A delegação em DOIS TEMPOS — decisão do owner, 18/09/2026: *"faça A"*.
 *
 * | tempo | o que acontece | por quê |
 * |---|---|---|
 * | 1º | a consulta parte e o laço FECHA na hora, com `SAIDA_EM_ANDAMENTO` | com a delegação aberta o provedor engole todo `*.append` e não verbaliza nem o `function_call_output` |
 * | — | a espera é acompanhada por `commentary.append` | com a sessão ociosa o append ECOA e o modelo FALA (medido: 7-8 deltas por sondagem) |
 * | 2º | o desfecho sai por `commentary.append`, sob a guarda da D-O-6 | o texto falado é o MESMO que foi para a tela, refeito a partir da origem |
 *
 * **Nunca `response.create` no meio** — ele abre outra delegação (15 pedidos →
 * 15 delegações, 0 falas) e recria o silêncio que isto remove.
 *
 * Desligado, o transporte volta byte a byte ao comportamento anterior: o laço
 * fecha no fim do turno, com o desfecho no `function_call_output`. É o que
 * torna a medição do ANTES reproduzível sem git.
 */
export const DELEGACAO_EM_DOIS_TEMPOS = false

/*
 * ★★★ [T111] DESLIGADO em 19/09/2026, por medição.
 *
 * O desenho em dois tempos respondia a `function_call` na hora com
 * `{status: 'em_andamento'}` para "devolver a sessão ao estado ocioso, que é
 * quando ela de fato fala". **A sessão não volta a ficar ociosa** — e agora se
 * sabe o que acontece de verdade:
 *
 * | fase | bytes RTP enviados | `usage.seconds` | o modelo falou? |
 * |---|---|---|---|
 * | ociosa | 27k → 91k, subindo | 15 → 44 | **4 de 4** |
 * | com a tool pendente | **congelado em 100.061** | **congelado em 57** | **0 de 4** |
 *
 * O braço B do mesmo experimento repetiu tudo com o porteiro do microfone
 * DESLIGADO (`track.enabled: true`): a mídia congelou igual. Não é a nossa
 * trilha. **O provedor congela a linha do tempo da sessão enquanto a chamada
 * não é resolvida de verdade.**
 *
 * E o modelo devolve o nosso "em andamento" de volta: na retomada aos 109 s
 * ele gera *"já disparei a consulta das suas tarefas em aberto e ela ainda
 * está em andamento"* — exatamente o que mandamos. Ele nunca engoliu nada;
 * estava obedecendo, inclusive à nossa instrução de abertura (*"espere calado
 * até o resultado chegar"*).
 *
 * ⚠️ `{status: 'em_andamento'}` **não é um acknowledgment** — é o RESULTADO da
 * tool. Um resultado que diz "não tenho resultado" deixa a chamada sem
 * desfecho para sempre, porque o desfecho verdadeiro ia por outro canal
 * (`commentary.append`) que, na semântica do protocolo, não é a resposta dela.
 *
 * O caminho clássico — `_responder(resultado real)` → `_criarResposta()` — já
 * existia aqui, testado, e é o que o protocolo espera. Ele volta a ser o único.
 */

// ★ D-13 — as remissões (o que a voz pode dizer) e a guarda que as trava moram
// no irmão `transporte-tempo-real-remissoes.js`. Reexportadas para quem consome
// a delegação não precisar saber disso — mesma cortesia do `-protocolo.js`.
export * from './transporte-tempo-real-remissoes'

/**
 * ★ N21 — o `PedidoDeDelegacao` do contrato (`contracts/transporte.md`, C-6).
 *
 * `texto` é o TURNO TRANSCRITO do consultor, sempre. Os `arguments` do modelo
 * viajam em `desambiguacao`, marcados como o que são: pista para desempatar,
 * nunca chave de busca. Sem turno transcrito **não há pedido** — é aqui que a
 * tentação mora (o `arguments` está ali, parece estruturado e confiável), e é
 * por isso que a recusa é uma exceção nomeada e não um `return null` discreto.
 */
export const montarPedidoDeDelegacao = ({
  demanda,
  texto,
  idCorrelacao,
  idSessao,
  superficie,
  sinal,
}) => {
  const turno = String(texto || '').trim()
  if (!turno) {
    throw new ErroDeVozTempoReal(
      MOTIVO_DEMANDA_SEM_TURNO,
      'Demanda sem turno transcrito: os `arguments` do modelo desambiguam, ' +
        'nunca fazem lookup.'
    )
  }
  const pista = demanda || {}
  return {
    texto: turno,
    idCorrelacao,
    idSessao,
    superficie,
    origem: ORIGEM_VOZ_TEMPO_REAL,
    sinal,
    desambiguacao: {
      ferramenta: pista.nome || '',
      argumentos: pista.argumentos || {},
    },
  }
}

const _novoControlador = () =>
  typeof globalThis.AbortController === 'function'
    ? new globalThis.AbortController()
    : null

/**
 * Drena o fluxo do `POST /chat`. Aceita `AsyncIterable` (o SSE de sempre),
 * `Promise` e texto — o contrato diz `AsyncIterable<string>`, mas quem
 * implementa a fachada ainda não existe ([T033]) e um consumidor que só sabe
 * uma das formas vira acoplamento escondido.
 *
 * `for await` está fora de propósito: o Babel deste projeto não transpila
 * tudo o que a sintaxe nova pede (é a mesma razão pela qual `?.` é proibido
 * aqui), e um laço de `.next()` não depende disso.
 */
/**
 * ★ [T033] A PROPOSTA que a escrita deixou esperando na tela.
 *
 * Os dois nomes são os que este repositório de fato usa: `preview_id` é o do
 * two-step do Modo Chat (`store/toolParts.js`) e `id_proposta` o do Modo Agente
 * (`store/state.js`). Aceitar os dois é o que evita a terceira grafia.
 *
 * ⚠️ **O contrato diz `delegar: (texto) => AsyncIterable<string>`**
 * (`contracts/transporte.md`), e string não carrega proposta. A divergência
 * está reportada: quem implementar a fachada ([T030b]) resolve com um objeto
 * `{texto, preview_id}` quando o turno produziu escrita. O caminho de string
 * continua valendo byte a byte.
 */
export const lerPropostaDoResultado = (resultado) => {
  if (!resultado || typeof resultado !== 'object') return null
  const id = resultado.preview_id || resultado.id_proposta
  if (!id) return null
  return { idProposta: String(id) }
}

/**
 * ⚠️ **Defeito achado na [T044] e corrigido aqui:** fazia `String(… || valor
 * || '')`, então objeto sem `texto` nem `response` virava `"[object Object]"`
 * — não-vazio, logo "pronto". Era a mentira que `_saidaDoDesfecho` diz querer
 * impedir: remeter a um cartão vazio soa igual ao caminho feliz.
 */
const _desfechoDeUmValor = (valor) => {
  const ehObjeto = Boolean(valor) && typeof valor === 'object'
  const texto = ehObjeto
    ? (valor.texto || valor.response || '')
    : (valor || '')
  return {
    texto: String(texto),
    proposta: lerPropostaDoResultado(valor),
    valor: lerValorDoResultado(valor),
  }
}

/**
 * Drena o fluxo e devolve `{texto, proposta}` — nunca a string crua: o
 * desfecho de um turno de ESCRITA não é texto, é uma decisão esperando na tela.
 */
const _drenar = async (fluxo) => {
  if (!fluxo) return { texto: '', proposta: null, valor: null }
  if (typeof fluxo[Symbol.asyncIterator] !== 'function') {
    return _desfechoDeUmValor(await fluxo)
  }
  const iterador = fluxo[Symbol.asyncIterator]()
  let texto = ''
  let passo = await iterador.next()
  while (!passo.done) {
    texto += String(passo.value || '')
    passo = await iterador.next()
  }
  // Um fluxo de STRINGS não carrega valor estruturado — e extrair número de
  // texto com regex seria adivinhar qual dos números do parágrafo é o fato.
  return { texto, proposta: null, valor: null }
}

/**
 * O executor. Uma instância por sessão — o estado dele (o que está em voo, o
 * que está pendente) é de SESSÃO, e herdá-lo faria a demanda de uma conversa
 * responder à de outra.
 *
 * @param {Object} params
 * @param {(evento: string, payload?: Object) => void} params.emitir ao `Destino`
 * @param {(mensagem: Object) => boolean} params.enviar põe no data channel
 * @param {() => string} params.textoDoTurno o turno transcrito do consultor
 * @param {() => Object} params.contexto `{idSessao, superficie, delegar}` —
 *   getter, porque o `id_sessao` só existe depois da negociação
 */
// eslint-disable-next-line max-statements
export const criarExecutorDeDelegacao = ({
  emitir,
  enviar,
  textoDoTurno,
  //  ★ [18/09/2026] O que VAI ao chat: o diálogo do turno, com quem disse cada
  //  coisa. Default = `textoDoTurno`, que mantém o comportamento de quem não
  //  passa o novo parâmetro (os testes antigos, entre outros).
  dialogoDoTurno = null,
  //  Chamado quando o turno é de fato DELEGADO — é o que impede o diálogo de
  //  ser reenviado inteiro na delegação seguinte.
  aoConsumirTurno = null,
  contexto,
}) => {
  let _emVoo = null
  let _pendentes = []
  let _sequencia = 0

  /**
   * ★ [T063] A voz que acompanha a espera. Quem a liga é `_delegarAoChat`
   * (a consulta entrou em voo) e quem a cala são `_concluir` e `abortar` —
   * os DOIS únicos caminhos que zeram `_emVoo`, e é por isso que o silêncio
   * depois do resultado não precisa de guarda própria lá dentro.
   *
   * `aoRecusar` é o `_registrarRecusa` daqui — declarado adiante no mesmo
   * escopo e só chamado por temporizador, muito depois de tudo inicializar.
   */
  const _acompanhamento = criarAcompanhamentoDaEspera({
    enviar,
    aoRecusar: (motivo, detalhe) => _registrarRecusa(motivo, detalhe),
    valorEmMaos: () => _valorParcialDoTurno(),
  })

  /**
   * ★ [T063, correção do owner] O valor que a consulta JÁ devolveu, enquanto
   * ela ainda corre — é o que autoriza a voz a adiantar o número.
   *
   * Lido do `ContextoDeSessao` por um getter OPCIONAL: o produtor natural é
   * `contexto-de-voz-tempo-real.js::lerDesfechoDoTurnoFalado`, que já sabe
   * tirar `{valor, tipo}` do cartão do turno — falta só a tela expor essa
   * leitura como getter parcial, e é uma linha em `view-chat.vue`.
   *
   * ⚠️ **Enquanto ela não existir, isto devolve `null` e a voz diz o ESTADO**
   * — que é o comportamento da primeira versão desta task, e é seguro. A
   * degradação é explícita de propósito: fingir um valor parcial que ninguém
   * produz seria inventar contrato, que é o erro que a [T044] nomeou.
   */
  const _valorParcialDoTurno = () => {
    const ler = _ctx().lerValorParcialDoTurno
    if (typeof ler !== 'function') return null
    return lerValorDoResultado(ler()) || null
  }

  const _ctx = () => (typeof contexto === 'function' && contexto()) || {}

  /**
   * Determinístico de propósito: `Math.random`/`Date.now` fariam a correlação
   * — que é a guarda do resultado atrasado — impossível de asserir num teste.
   */
  const _proximaCorrelacao = () => {
    _sequencia += 1
    return `${_ctx().idSessao || 'sessao'}#${_sequencia}`
  }

  /** As duas testemunhas do modo, as duas do próprio provedor. */
  const _conferirModo = (modo) => {
    if (modo === MODO_DELEGACAO_RESPONSES) return
    marcarFalhaDaMedicao(MOTIVO_MODO_DE_DELEGACAO)
    emitir('queda', { motivo: MOTIVO_MODO_DE_DELEGACAO })
  }

  /**
   * O eco da criação. ⚠️ Quando `session.started` NÃO traz `delegation`, não
   * há veredito: o único eco medido é de uma sessão que nasceu configurada
   * (G-12 §4, n=1), e não foi medido se o eco omite o campo numa sessão
   * default. Chamar a omissão de "modo errado" seria inventar o que não se
   * mediu — a testemunha forte chega no gatilho, que traz `target` em 6/6.
   */
  const _aoIniciarSessao = (evento) => {
    const sessao = (evento && evento.session) || {}
    const delegacao = sessao.delegation
    if (!delegacao) return
    _conferirModo(delegacao.type)
  }

  const _aoCriarDelegacao = (evento) => {
    const delegacao = (evento && evento.delegation) || {}
    _conferirModo(delegacao.target)
  }

  const _esquecerPendente = (callId) => {
    _pendentes = _pendentes.filter((id) => id !== callId)
  }

  /**
   * ★ [T044/T072] `origem` é o que o TURNO devolveu — `{texto, valor, tipo}` —
   * e é ela que torna os invariantes verificáveis: a guarda refaz o
   * arredondamento (D-O-3) e a montagem do texto (D-O-6) a partir daqui, em
   * vez de acreditar no que a saída diz. As recusas não têm origem, e não
   * podem mesmo ter: elas não falam número nem conteúdo nenhum.
   */
  /**
   * ★★★ [T111] O ÚNICO ponto por onde uma saída chega ao modelo — e ele agora
   * carrega o DESFECHO, não mais uma remissão provisória.
   *
   * Três coisas que este corpo garante, e que só valem juntas:
   *
   * 1. **A guarda D-13/D-O-3 continua antes do envio.** Nada chega ao modelo
   *    sem remeter à origem.
   * 2. **Recusa vira RECUSA REGISTRADA, nunca exceção solta.** `conferirQueRemete`
   *    e `enviar` lançam, e daqui para cima estamos dentro de um `await`: uma
   *    exceção escapando emudeceria a voz **sem deixar rastro**, que é o pior
   *    desfecho possível. Medido: sem este `try`, o caso da guarda recusando o
   *    desfecho não produzia recusa nenhuma — zero eventos de erro.
   * 3. **A pendência só é esquecida quando o envio DEU CERTO.** `enviar`
   *    devolve `false` com o canal fechado; esquecer mesmo assim deixaria o
   *    `call_id` sem resposta no servidor e a sessão travada (N20), com o
   *    nosso lado achando que respondeu.
   */
  const _responder = (callId, saida, origem) => {
    try {
      conferirQueRemete(saida, origem)
      const foi = enviar(montarResultadoDaFuncao({ callId, saida }))
      if (foi === false) {
        _registrarRecusa(
          MOTIVO_DELEGACAO_FALHOU,
          `o resultado da chamada ${callId} não saiu no fio — canal fechado`
        )
        return false
      }
      _esquecerPendente(callId)
      return true
    } catch (erro) {
      _registrarRecusa(
        MOTIVO_DELEGACAO_FALHOU,
        `o resultado da chamada ${callId} foi recusado: ${(erro && erro.message) || erro}`
      )
      return false
    }
  }

  /**
   * ★ N20 do nosso lado: `response.create` com pendência é recusado AQUI, sem
   * ir ao fio. O provedor recusaria (*"Submit the pending function call
   * outputs before response.create."*) — e o custo de descobrir isso lá é uma
   * sessão paga travada.
   *
   * Recusa em SILÊNCIO de propósito: o único caminho que chega aqui com
   * pendência é a demanda recusada por concorrência, e ela já registrou o
   * `erro`. Emitir de novo seria o mesmo fato duas vezes no mesmo instante.
   */
  /**
   * ★ [spec-008 T093] A recusa do pedido de turno passa a DEIXAR RASTRO.
   *
   * ⚠️ As duas guardas abaixo são corretas e ficam — mas elas recusavam **em
   * silêncio**, e isso torna indistinguíveis dois desfechos que o G6 precisa
   * separar:
   *
   * | o que aconteceu | como aparecia |
   * |---|---|
   * | o pedido SAIU e o provedor não verbalizou | nada no fio além do append |
   * | o pedido foi RECUSADO aqui e nunca saiu | idêntico |
   *
   * Medido em 19/09: a resposta chegou à tela (854 tarefas) e a voz não a
   * falou — e sem este rastro não dá para dizer se o turno chegou a ser
   * pedido. `window.__voz` só existe sob a bancada de teste; em produção isto
   * é um `if` que nunca entra.
   */
  const _anotarNaBancada = (evento, extra) => {
    const banca = globalThis.__voz
    if (!banca || !Array.isArray(banca.microfone)) return
    banca.microfone.push({ evento: evento, ms: Date.now(), ...(extra || {}) })
  }

  const _criarResposta = () => {
    if (_pendentes.length) {
      _anotarNaBancada('turno_recusado_por_pendentes', { pendentes: _pendentes.length })
      return false
    }
    // ★ [T073] E NUNCA com uma consulta em voo. Um pedido de turno abre outra
    // delegação (medido), e delegação aberta engole o append do desfecho que
    // está por vir — ou seja, a recusa de uma segunda demanda emudeceria a
    // resposta da PRIMEIRA. Quem chegou aqui com consulta em voo fala por
    // append, via `_pedirTurnoOuFalar`.
    if (_emVoo) {
      _anotarNaBancada('turno_recusado_por_consulta_em_voo')
      return false
    }
    _anotarNaBancada('turno_pedido')
    enviar(montarPedidoDeResposta())
    return true
  }

  /**
   * ★ [T073] Fecha uma RECUSA do jeito que o estado da sessão permite.
   *
   * | estado | o que acontece |
   * |---|---|
   * | ocioso | pede turno (comportamento de sempre) |
   * | consulta em voo | fala por append — pedir turno reabriria a delegação |
   * | outro `function_call` pendente | nada: com pendência, o append é engolido |
   */
  const _pedirTurnoOuFalar = (saida) => {
    if (_criarResposta()) return
    if (_emVoo) _falarODesfecho(saida)
  }

  /**
   * ★ [T073] O segundo tempo: o desfecho empurrado ao canal, com a sessão
   * ociosa.
   *
   * A `origem` viaja junto porque é ela que a guarda usa para REFAZER a fala
   * da consolidação e exigir igualdade — o mesmo invariante da D-O-6, no outro
   * caminho. Sem ela, um texto na fala não teria contra o que ser conferido.
   *
   * Best-effort, como todo o canal: `enviar` devolve `false` com o canal
   * fechado e a sessão segue. O que não pode acontecer é o desfecho sumir em
   * silêncio — e não some: a tela já tem o cartão, que é a fonte auditável.
   */
  const _falarODesfecho = (saida, origem) => {
    const conteudo = falaDoDesfecho(saida)
    if (!conteudo) {
      // ★ [T093] Desfecho sem frase é outro jeito de a voz emudecer, e ele
      // também era invisível: sai daqui sem append e sem pedido de turno.
      _anotarNaBancada('desfecho_sem_frase')
      return
    }
    _anotarNaBancada('desfecho_montado', { tamanho: String(conteudo).length })
    // ★★ [spec-008 T097] `enviar` LANÇA quando a guarda D-13 recusa a fala
    // (`conferirQueAFalaRemete` lança antes do `_cano.enviar`), e esta chamada
    // não tinha `try`.
    //
    // ⚠️ Sem ele, uma recusa aqui **emudece a voz sem deixar registro**: a
    // exceção sai de dentro de um `await` — `_emVoo` já foi zerado, `_concluir`
    // não tem quem a pegue —, o `_criarResposta()` abaixo nunca roda, e o
    // consultor fica com o dado na tela e o silêncio no ouvido. É exatamente o
    // sintoma do G6, e ele era indistinguível de "o provedor não verbalizou".
    //
    // O acompanhamento ([T063]) já trata a mesma recusa com `try`; o desfecho
    // não tratava. Aqui a recusa vira registro nomeado e a sessão segue.
    try {
      enviar(
        montarAppendDeFala({
          tipo: EVENTO_APPEND_DE_COMENTARIO,
          conteudo,
          idDaDelegacao: null,
        }),
        origem
      )
      _anotarNaBancada('desfecho_enviado')
    } catch (erro) {
      _anotarNaBancada('desfecho_RECUSADO_pela_guarda', {
        motivo: String((erro && erro.motivo) || (erro && erro.message) || erro).slice(0, 120),
      })
      _registrarRecusa(
        (erro && erro.motivo) || MOTIVO_DELEGACAO_FALHOU,
        'a guarda recusou a fala do DESFECHO — o dado está na tela e a voz calou'
      )
      return
    }
    // ★ [T074, medido em 18/09/2026] O append do desfecho é ACEITO (1 enviado
    // → 1 eco) **e o modelo o FALA**: na `promessa-exec200` os deltas saíram
    // 561 ms depois do append, com o conteúdo do append. É o único ponto do
    // fio em que isso foi observado. Pedir o turno aqui é seguro porque:
    //
    // | quando | efeito medido |
    // |---|---|
    // | durante a espera, laço ABERTO | 15 pedidos → 15 delegações novas, 0 falas |
    // | no desfecho, laço FECHADO | ← é isto que a execução 3 mede |
    //
    // UM só, e só aqui: o conteúdo já foi entregue e ecoado; o pedido serve
    // para o turno existir. Se a medição disser que ele reabre a delegação sem
    // produzir fala, esta linha sai e o caminho volta a ser só o append.
    //
    // ★ [T097] Anotado com nome PRÓPRIO: o rastro é uma lista plana, e um
    // `turno_pedido` genérico não dizia se veio daqui ou da saudação. A
    // conclusão "o front fez tudo certo" dependia dessa distinção.
    const pediu = _criarResposta()
    _anotarNaBancada(pediu ? 'desfecho_turno_pedido' : 'desfecho_turno_NAO_pedido')
  }

  /**
   * D-11: resultado cuja correlação não é a corrente é DESCARTADO, nunca
   * falado. Com a serialização de N20 isto só dispara em teardown/aborto —
   * mas é exatamente aí que a rede já pode ter entregue a resposta da consulta
   * que o consultor cancelou.
   */
  const _correlacaoCorrente = (idCorrelacao) =>
    Boolean(_emVoo) && _emVoo.idCorrelacao === idCorrelacao

  const _concluir = ({ callId, idCorrelacao, saida, erro, proposta, origem }) => {
    // ★ [T099] Esta guarda era a SEGUNDA saída sem luz do caminho do desfecho.
    // Ela descarta em silêncio — e descartar em silêncio é exatamente o que
    // se vê quando o desfecho nunca chega ao fio: indistinguível de "a espera
    // não resolveu". As duas hipóteses precisam de rastro DIFERENTE, senão a
    // atribuição vira eliminação (o erro que já custou um veredito nesta spec).
    if (!_correlacaoCorrente(idCorrelacao)) {
      _anotarNaBancada('desfecho_DESCARTADO', {
        motivo: _emVoo ? 'correlacao_de_outro_turno' : 'nada_em_voo',
        idCorrelacao: String(idCorrelacao || ''),
      })
      return
    }
    const fechouCedo = Boolean(_emVoo && _emVoo.lacoFechadoCedo)
    _emVoo = null
    // [T063] O resultado chegou: para de dizer que está em andamento. É ESTE
    // `parar()` que faz o silêncio depois do desfecho — a mutação que o tira
    // deixa cinco falas de espera vazarem para depois do resultado.
    _acompanhamento.parar()
    if (erro) _registrarRecusa(MOTIVO_DELEGACAO_FALHOU, (erro && erro.message) || '')
    // ★ [T033] A decisão sobe para a TELA, que é onde ela se resolve. Só com a
    // correlação corrente: a proposta de um turno cancelado não pinta cartão.
    if (proposta) emitir('propostaAguardando', proposta)
    // ★ [T073] Dois tempos: se o laço JÁ fechou quando a consulta partiu, não
    // há `function_call_output` pendente — o desfecho sai pelo canal que, com
    // a sessão ociosa, de fato fala. E NÃO se pede turno depois: `response.
    // create` abre outra delegação (medido: 15 pedidos → 15 delegações), e é
    // ela que emudeceria o append seguinte.
    if (fechouCedo) {
      _falarODesfecho(saida, origem)
      return
    }
    _responder(callId, saida, origem)
    _criarResposta()
  }

  /**
   * ★ [T050] Toda recusa passa por AQUI, e por aqui ela fica registrada.
   *
   * As quatro portas emitiam `erro` e mais nada. `emitir` é
   * `if (typeof _destino[evento] !== 'function') return`, e o `Destino` real
   * da tela **não tinha morador para `erro`** — então a recusa não chegava ao
   * consultor NEM à medição, e a única testemunha do defeito era o silêncio.
   *
   * `marcarFalhaDaMedicao` é o canal nomeado do módulo (N10) e o mesmo que o
   * modo de delegação já usava: `console.warn` não serve, porque o
   * `tests/unit/setup.js` deste repo o faz REPROVAR o teste — de propósito.
   */
  const _registrarRecusa = (motivo, detalhe) => {
    marcarFalhaDaMedicao(motivo)
    emitir('erro', { motivo, detalhe: detalhe || '' })
  }

  const _recusarPorConcorrencia = (demanda) => {
    _registrarRecusa(MOTIVO_DELEGACAO_EM_CURSO, demanda.nome)
    _responder(demanda.callId, SAIDA_DE_RECUSA_POR_CONCORRENCIA)
    // A continuação é PEDIDA e a guarda a recusa — é ela que impede o
    // `response.create` de sair com a primeira em voo. Sem esta chamada a
    // guarda nunca rodaria, e "serial por sessão" seria uma linha de código
    // que nenhum teste consegue deixar vermelha.
    //
    // ★ [T073] E quando a guarda recusa, a recusa ainda assim é DITA — por
    // append, que é o canal que funciona com a sessão ociosa. Antes disto ela
    // saía como pedido de turno e reabria a delegação, emudecendo o desfecho
    // da consulta que estava em voo.
    _pedirTurnoOuFalar(SAIDA_DE_RECUSA_POR_CONCORRENCIA)
  }

  /**
   * ★ [T040b] N21 — a pista contradiz a fala. Recusa NOMEADA, com a CHAVE do
   * argumento no detalhe e nunca o valor: o valor é o palpite do roteador, e
   * repeti-lo para cima é o que a guarda existe para impedir.
   */
  const _recusarPorIdentificador = (demanda, chave) => {
    _registrarRecusa(MOTIVO_IDENTIFICADOR_NAO_CONFERE, `${demanda.nome}:${chave}`)
    _responder(demanda.callId, SAIDA_PEDE_IDENTIFICADOR)
    _pedirTurnoOuFalar(SAIDA_PEDE_IDENTIFICADOR)
  }

  const _recusarSemTurno = (demanda) => {
    _registrarRecusa(MOTIVO_DEMANDA_SEM_TURNO, demanda.nome)
    _responder(demanda.callId, SAIDA_SEM_TURNO)
    _pedirTurnoOuFalar(SAIDA_SEM_TURNO)
  }

  /**
   * O que a voz diz, por desfecho.
   *
   * ★ [T033] Proposta pendente vence texto: o turno pediu ESCRITA, ela foi
   * pré-visualizada e espera decisão — dizer "pronto, está no cartão" ali seria
   * afirmar que a ação aconteceu.
   *
   * ★ [T044] D-O-3 — com número legível, a voz FALA o número arredondado por
   * NÓS; sem ele, volta a remeter. O texto da fala passa por `formatarParaFala`
   * e por mais nada: não existe aqui caminho que escreva número à mão, e é isso
   * que faz `conferirQueRemete` passar sempre no caminho feliz e falhar sempre
   * num enxerto.
   *
   * ★ **[T072] D-O-6 — com TEXTO na tela, a voz CONSOLIDA o texto**, que é o
   * que o owner pediu: *"se passar pela voz com a voz consolidando aquele
   * texto em uma conversa fluida resolve"*. Ele vem antes do número sozinho
   * porque `montarSaidaDeConsolidacao` leva os dois quando os dois existem —
   * a prosa que o consultor está lendo e, em `valor_falado`, o número já
   * arredondado por nós, que é a D-O-3 intacta dentro do desfecho novo.
   *
   * ⚠️ **A proposta continua vencendo tudo** (US-5/D-5): o turno de ESCRITA
   * deixou uma decisão esperando na tela, e consolidar a prosa dele soaria
   * como ação consumada. Esta linha é a primeira de propósito.
   *
   * Delegação que não devolveu NADA não é sucesso: remeter ao cartão com o
   * cartão vazio é a pior mentira possível, porque soa igual ao caminho feliz.
   * Valor SEM texto é conteúdo — o cartão tem o que mostrar —, então conta.
   */
  const _saidaDoDesfecho = (desfecho) => {
    if (desfecho.proposta) return SAIDA_DECISAO_NA_TELA
    const origem = desfecho.valor || {}
    const valorFalado = formatarParaFala(origem.valor, { tipo: origem.tipo })
    const consolidada = montarSaidaDeConsolidacao({
      texto: desfecho.texto,
      valorFalado,
    })
    if (consolidada) return consolidada
    if (valorFalado) return montarSaidaComValor({ valorFalado })
    return SAIDA_DE_FALHA
  }

  /**
   * ★ [T072] A ORIGEM contra a qual a guarda refaz a montagem — `{texto,
   * valor, tipo}`.
   *
   * Ela passou a carregar o `texto` porque a D-O-6 o deixa atravessar, e um
   * conteúdo que atravessa sem origem contra o que conferir é exatamente o
   * conteúdo que veio de lugar nenhum. As chaves do número continuam as
   * mesmas: quem não tem valor manda só o texto, e `conferirQueRemete` escolhe
   * o regime pela CHAVE da saída, não por esta.
   */
  const _origemDoDesfecho = (desfecho) => {
    const valor = desfecho.valor || {}
    return {
      texto: desfecho.texto || '',
      valor: valor.valor,
      tipo: valor.tipo,
    }
  }

  const _delegarAoChat = async (demanda) => {
    const ctx = _ctx()
    const idCorrelacao = _proximaCorrelacao()
    const controlador = _novoControlador()
    const pedido = montarPedidoDeDelegacao({
      demanda,
      texto: (dialogoDoTurno || textoDoTurno)(),
      idCorrelacao,
      idSessao: ctx.idSessao,
      superficie: ctx.superficie,
      sinal: controlador ? controlador.signal : null,
    })
    _emVoo = { callId: demanda.callId, idCorrelacao, controlador, lacoFechadoCedo: false }
    // O turno foi para o chat: o diálogo acumulado até aqui já viajou, e
    // mantê-lo faria a próxima delegação repetir a conversa inteira.
    if (aoConsumirTurno) aoConsumirTurno()
    // ★ [T073] PRIMEIRO TEMPO — o laço fecha aqui, com a consulta recém-
    // disparada, e NÃO no fim do turno.
    //
    // ⚠️ **[T074] O QUE ISTO NÃO COMPROU — medido em 18/09/2026, 3 sessões
    // pagas pós-T073** (`reports/voz-tempo-real-promessa/promessa-exec200..202`):
    //
    // | execução | appends na ESPERA | ecos | a IA falou a resposta? |
    // |---|---|---|---|
    // | 200 (consulta falhou em 0,5 s) | 1 (o DESFECHO) | **1** | falou a FALHA |
    // | 201 (espera de 223 s) | **17** | **0** | **não** |
    // | 202 (espera de 129 s) | **10** | **0** | **não** |
    //
    // Fechar o laço no disparo **não** fez o provedor aceitar os appends da
    // ESPERA: 27 enviados em duas sessões, zero ecoados — o mesmo 26→0 de
    // antes. O único append ecoado foi o do DESFECHO, 0,5 s depois do ack e
    // com a consulta já concluída; esse o modelo aceitou **e falou** (deltas
    // 561 ms depois — é o primeiro registro de fala empurrada nesta sessão).
    //
    // Leitura honesta: o que os dois tempos entregam é o DESFECHO falável.
    // A conversa DURANTE a espera continua muda, e a resposta com o dado
    // nunca foi falada em nenhuma das três execuções. Não desligue isto
    // esperando recuperar a espera — ela nunca funcionou.
    //
    // Enquanto a delegação está aberta o provedor engole todo `*.append`
    // (medido 17/09: 3 sondagens, 0 ecos, 0 deltas) e não verbaliza nem o
    // próprio `function_call_output` (14/14 execuções). Com a sessão ociosa as
    // MESMAS sondagens falam (7-8 deltas). Daí fechar AQUI, e não no fim.
    //
    // ⚠️ **A posição desta linha em relação a `_acompanhamento.comecar()` é
    // indiferente** — e isto está escrito porque a versão anterior deste
    // comentário afirmava o contrário. O acompanhamento ARMA um temporizador
    // (8 s até a primeira frase); ele não fala nada de forma síncrona. Trocar
    // as duas linhas de lugar não muda uma vírgula do fio, e há mutação
    // provando isso. O que importa é fechar no DISPARO da consulta, não no
    // desfecho dela.
    //
    // E aqui NÃO se chama `_criarResposta()`: `response.create` abre outra
    // delegação (medido: 15 pedidos → 15 delegações), que emudeceria tudo o
    // que vem depois. Quem faz o modelo falar é o append, não o pedido de
    // turno.
    if (DELEGACAO_EM_DOIS_TEMPOS) {
      _responder(demanda.callId, SAIDA_EM_ANDAMENTO)
      _emVoo.lacoFechadoCedo = true
    }
    // [T063] A partir daqui o consultor está esperando — e a voz passa a
    // acompanhar em vez de guardar tudo para o fim.
    _acompanhamento.comecar()
    try {
      const desfecho = await _drenar(ctx.delegar(pedido))
      _concluir({
        callId: demanda.callId,
        idCorrelacao,
        saida: _saidaDoDesfecho(desfecho),
        proposta: desfecho.proposta,
        origem: _origemDoDesfecho(desfecho),
      })
    } catch (erro) {
      _concluir({
        callId: demanda.callId,
        idCorrelacao,
        saida: SAIDA_DE_FALHA,
        erro,
      })
    }
  }

  /**
   * Sem caminho de delegação registrado ([T033]/[T030b] ainda por vir), o laço
   * TEM de fechar mesmo assim: um `function_call` sem resposta trava a sessão
   * (N20) e deixa o consultor esperando calado.
   */
  const _recusarSemCaminho = (demanda) => {
    if (typeof _ctx().delegar === 'function') return false
    _registrarRecusa(
      MOTIVO_DELEGACAO_FALHOU,
      'Nenhum caminho de delegação registrado na sessão.'
    )
    _responder(demanda.callId, SAIDA_DE_FALHA)
    _pedirTurnoOuFalar(SAIDA_DE_FALHA)
    return true
  }

  /**
   * As quatro portas de recusa, na ORDEM em que elas valem. `true` = a demanda
   * já foi respondida e o laço já fechou.
   *
   * A ordem não é arbitrária: sem turno vence a reconciliação (não há com que
   * reconciliar), e a concorrência vence tudo (com uma pendente, nem se olha a
   * segunda).
   */
  const _recusouAntesDeConsultar = (demanda) => {
    if (_emVoo) {
      _recusarPorConcorrencia(demanda)
      return true
    }
    const turno = String(textoDoTurno() || '').trim()
    if (!turno) {
      _recusarSemTurno(demanda)
      return true
    }
    // ★ [T040b] Reconcilia ANTES de consultar: sem correspondência exata entre
    // o identificador da pista e o que o consultor falou, a voz pergunta.
    const veredito = reconciliarIdentificador({ demanda, turno })
    if (!veredito.ok) {
      _recusarPorIdentificador(demanda, veredito.chave)
      return true
    }
    return _recusarSemCaminho(demanda)
  }

  const _atender = async (demanda) => {
    _pendentes.push(demanda.callId)
    if (_recusouAntesDeConsultar(demanda)) return
    await _delegarAoChat(demanda)
  }

  const _aoReceberEventoDaResponses = (evento) => {
    const demanda = lerDemandaDeFuncao(evento)
    if (!demanda) return
    _atender(demanda).catch((erro) => {
      _registrarRecusa(
        (erro && erro.motivo) || MOTIVO_DELEGACAO_FALHOU,
        (erro && erro.message) || ''
      )
    })
  }

  /**
   * Aborta o que está em voo e esquece as pendências. Chamado no fim da sessão
   * e na abertura da seguinte — depois dele, o resultado que a rede ainda
   * entregar cai fora pela correlação.
   */
  const abortar = () => {
    // ★ [T099] Abortar COM consulta em voo mata o desfecho daquele turno —
    // e é o único outro caminho que zera `_emVoo`. Sem este registro, o
    // descarte em `_concluir` não teria de quem ser filho.
    if (_emVoo) _anotarNaBancada('abortou_com_consulta_em_voo')
    if (_emVoo && _emVoo.controlador) _emVoo.controlador.abort()
    _emVoo = null
    _pendentes = []
    // [T063] Teardown/abertura da sessão seguinte: um temporizador que
    // sobrevivesse falaria "a consulta continua em andamento" numa sessão
    // que não tem consulta nenhuma.
    _acompanhamento.parar()
  }

  /** Só diagnóstico — nunca para decidir comportamento. */
  const emVoo = () => Boolean(_emVoo)
  const pendentes = () => _pendentes.slice()
  const acompanhamentosFalados = _acompanhamento.jaFalados

  return {
    aoIniciarSessao: _aoIniciarSessao,
    aoCriarDelegacao: _aoCriarDelegacao,
    aoReceberEventoDaResponses: _aoReceberEventoDaResponses,
    abortar,
    emVoo,
    pendentes,
    acompanhamentosFalados,
  }
}
